import {
  IAssemblyModelInfo,
  IDoorWindowModelInfo,
  IFurnitureModelInfo,
  IMultiModelData,
  ISeparateWallModelInfo,
  TMultiModelDataOf
} from '@/editor/data/multiModel/multiModel';
import { globalStores } from '@/editor/stores';
import { EEntityType } from '@/editor/types';

/**
 * 是否只含框选能选中的构件（家具/组合/隔墙）。
 * 门窗多选来自「选同款」，数据由 modifyMultiDoorWindow 自己维护，和这条路径互斥。
 */
export function isMultiSelectFurnitureAssemblyOrSepWallOnly(
  multiModelData?: Pick<IMultiModelData, 'modelInfos'> | null
): multiModelData is TMultiModelDataOf<IFurnitureModelInfo | IAssemblyModelInfo | ISeparateWallModelInfo> {
  if (!multiModelData || multiModelData.modelInfos.length === 0) {
    return false;
  }

  return multiModelData.modelInfos.every((item) =>
    [EEntityType.Furniture, EEntityType.Assembly, EEntityType.SeparateWall].includes(item.type)
  );
}

export function isMultiSelectFurnitureOrAssemblyOnly(
  multiModelData?: Pick<IMultiModelData, 'modelInfos'> | null
): multiModelData is TMultiModelDataOf<IFurnitureModelInfo | IAssemblyModelInfo> {
  if (!multiModelData) {
    return false;
  }

  if (multiModelData.modelInfos.length === 0) {
    return false;
  }

  if (
    multiModelData.modelInfos.filter((item) => ![EEntityType.Furniture, EEntityType.Assembly].includes(item.type))
      .length > 0
  ) {
    return false;
  }

  return true;
}

export function isMultiSelectDoorWindowOnly(
  multiModelData?: Pick<IMultiModelData, 'modelInfos'> | null
): multiModelData is TMultiModelDataOf<IDoorWindowModelInfo> {
  if (!multiModelData) {
    return false;
  }

  if (multiModelData.modelInfos.length === 0) {
    return false;
  }

  if (multiModelData.modelInfos.filter((item) => item.type !== EEntityType.DoorWindow).length > 0) {
    return false;
  }

  return true;
}

export function isModelInfoListAreSameModelFurniture(
  multiModelData?: Pick<IMultiModelData, 'modelInfos'> | null
): multiModelData is TMultiModelDataOf<IFurnitureModelInfo | IAssemblyModelInfo> {
  if (
    !multiModelData ||
    multiModelData.modelInfos.length === 0 ||
    !isMultiSelectFurnitureOrAssemblyOnly(multiModelData)
  ) {
    return false;
  }

  const modelIdSet = new Set<string>();
  let hasAssemblyFurniture = false;
  let hasMoreThanOneModel = false;
  for (const { id, type } of multiModelData.modelInfos) {
    switch (type) {
      case EEntityType.Furniture: {
        const furnitureData = globalStores.modelStore.getFurnitureData(id);

        if (furnitureData) {
          modelIdSet.add(furnitureData.modelId);
        }

        if (!hasMoreThanOneModel && modelIdSet.size > 1) {
          hasMoreThanOneModel = true;
        }

        if (!hasAssemblyFurniture) {
          hasAssemblyFurniture = globalStores.assemblyStore.isAssemblyFuniture(id);
        }
        break;
      }
      case EEntityType.Assembly: {
        const assemblyData = globalStores.assemblyStore.getAssemblyData(id);
        if (!hasMoreThanOneModel && assemblyData) {
          hasMoreThanOneModel = true;
        }
        break;
      }
    }
    if (hasMoreThanOneModel && hasAssemblyFurniture) {
      break;
    }
  }

  return !hasMoreThanOneModel && !hasAssemblyFurniture;
}

export function isMultiSelectedSameDoorWindow(multiModelData?: IMultiModelData | null) {
  if (!multiModelData) return false;
  let isSameDoorWindow = false;
  let firstType = '';

  for (const { id, type } of multiModelData.modelInfos) {
    if (type !== EEntityType.DoorWindow) break;

    const doorWindowData = globalStores.modelStore.getWindowData(id);
    if (!doorWindowData) continue;

    const { modelId } = doorWindowData;
    if (!firstType) {
      firstType = modelId;
    } else {
      isSameDoorWindow = modelId === firstType;
      if (modelId !== firstType) break;
    }
  }

  return isSameDoorWindow;
}
